import React, { useState, useEffect } from "react";
import { Button, Card, message, Spin, Typography, Space, Alert, Popconfirm, InputNumber, DatePicker, Form, Select } from "antd";
import { DownloadOutlined, PlayCircleOutlined, StopOutlined } from "@ant-design/icons";
import { authAxios } from "../utils/session_utils";
import styled from "@emotion/styled";
import { useOutletContext } from "react-router-dom";
import { LanguageSelectItem, SeriesSelectItem } from "../custom-components/form/fliter";

const { Title, Text } = Typography;

const Container = styled.div`
	padding: 24px;
	max-width: 800px;
	margin: 0 auto;
`;

const StatusCard = styled(Card)`
	margin-top: 24px;
`;

const BulkDescriptionsComponent: React.FC = () => {
	const {
		languagesList,
		seriesList,
	}: any = useOutletContext();
	const [loading, setLoading] = useState(false);
	const [operationId, setOperationId] = useState<string | null>(null);
	const [jobStatus, setJobStatus] = useState<any>(null);
	const [polling, setPolling] = useState(false);
	const [form] = Form.useForm();

	// Poll for job status
	useEffect(() => {
		if (!operationId || !polling) return;

		const pollInterval = setInterval(async () => {
			try {
				const response = await authAxios({
					method: "GET",
					url: `${process.env.REACT_APP_BACK_END_API}/api/jobs/${operationId}`,
				});

				// Handle nested status structure: response.data.status.status or response.data.status
				const jobData = response.data?.status || response.data;
				const statusString = typeof jobData === 'string' ? jobData : jobData?.status;
				
				setJobStatus(jobData);

				// Stop polling if job is complete, failed, or cancelled
				if (
					statusString === "COMPLETED" ||
					statusString === "COMPLETED_WITH_ERRORS" ||
					statusString === "FAILED" ||
					statusString === "CANCELLED"
				) {
					setPolling(false);
					clearInterval(pollInterval);
					
					if (statusString === "COMPLETED" || statusString === "COMPLETED_WITH_ERRORS") {
						message.success("Bulk description generation completed!");
					} else if (statusString === "CANCELLED") {
						message.warning("Bulk description generation was cancelled");
					} else {
						message.error("Bulk description generation failed");
					}
				}
			} catch (error) {
				console.error("Error polling job status:", error);
				setPolling(false);
				clearInterval(pollInterval);
			}
		}, 3000); // Poll every 3 seconds

		return () => clearInterval(pollInterval);
	}, [operationId, polling]);

	const handleStartGeneration = async () => {
		try {
			const values = await form.validateFields();
			const { limit, createdAfterDate, languages, series } = values;
			
			// Prepare request body
			const requestBody: any = {
				limit, languages, series
			};
			
			
			if (createdAfterDate) {
				requestBody.createdAfterDate = createdAfterDate.format('YYYY-MM-DD');
			}
			
			setLoading(true);
			const response = await authAxios({
				method: "POST",
				url: `${process.env.REACT_APP_BACK_END_API}/snowflake/bulk-descriptions/generate`,
				data: requestBody,
			});

			if (response.data?.success) {
				setOperationId(response.data.operationId);
				setPolling(true);
				message.success("Bulk description generation job started!");
			} else {
				message.error(response.data?.error || "Failed to start job");
			}
		} catch (error: any) {
			console.error("Error starting bulk description generation:", error);
			message.error(
				error?.response?.data?.error || "Failed to start bulk description generation"
			);
		} finally {
			setLoading(false);
		}
	};

	const handleDownloadCSV = () => {
		const csvUrl = jobStatus?.csvUrl || jobStatus?.metadata?.csvUrl;
		if (csvUrl) {
			window.open(csvUrl, "_blank");
		} else {
			message.warning("CSV file is not ready yet");
		}
	};

	const handleCancelJob = async () => {
		if (!operationId) return;
		
		try {
			const response = await authAxios({
				method: "DELETE",
				url: `${process.env.REACT_APP_BACK_END_API}/api/jobs/${operationId}`,
			});

			if (response.data?.success) {
				message.success("Job cancellation requested");
				setPolling(false);
				// Refresh status to show cancelled state
				setTimeout(() => {
					const pollStatus = async () => {
						try {
							const statusResponse = await authAxios({
								method: "GET",
								url: `${process.env.REACT_APP_BACK_END_API}/api/jobs/${operationId}`,
							});
							const jobData = statusResponse.data?.status || statusResponse.data;
							setJobStatus(jobData);
						} catch (error) {
							console.error("Error fetching job status:", error);
						}
					};
					pollStatus();
				}, 1000);
			} else {
				message.error(response.data?.error || "Failed to cancel job");
			}
		} catch (error: any) {
			console.error("Error cancelling job:", error);
			message.error(
				error?.response?.data?.error || "Failed to cancel job"
			);
		}
	};

	const getStatusMessage = () => {
		if (!jobStatus) return null;

		// Handle nested status structure
		const status = typeof jobStatus === 'string' 
			? jobStatus 
			: (jobStatus.status || jobStatus);
		const metadata = jobStatus.metadata || (typeof jobStatus === 'object' ? jobStatus : {});

		switch (status) {
			case "QUEUED":
			case "PENDING":
				return <Alert message="Job is queued and waiting to start" type="info" />;
			case "PROCESSING":
				return (
					<Alert
						message={`Processing... ${metadata.processed || 0} of ${metadata.totalVideos || 0} videos processed`}
						type="info"
						showIcon
					/>
				);
			case "COMPLETED":
				return (
					<Alert
						message={`Completed successfully! Processed ${metadata.totalRows || 0} videos. ${metadata.generatedCount || 0} videos had descriptions generated from captions, ${metadata.cachedCount || 0} videos had descriptions cached, ${metadata.copiedCount || 0} videos had descriptions copied, ${metadata.shortenedCount || 0} videos had descriptions shortened, ${metadata.noCaptionsCount || 0} videos had no captions available.`}
						type="success"
						showIcon
					/>
				);
			case "COMPLETED_WITH_ERRORS":
				return (
					<Alert
						message={`Completed with errors. ${metadata.totalRows || 0} videos processed.`}
						type="warning"
						showIcon
					/>
				);
			case "FAILED":
				return (
					<Alert
						message={`Job failed: ${jobStatus.message || "Unknown error"}`}
						type="error"
						showIcon
					/>
				);
			case "CANCELLED":
				return (
					<Alert
						message={`Job cancelled: ${jobStatus.message || "Cancelled by user"}`}
						type="warning"
						showIcon
					/>
				);
			default:
				return <Alert message={`Status: ${status}`} type="info" />;
		}
	};

	return (
		<Container>
			<Card>
				<Title level={2}>Bulk Description Generator</Title>
				<Text>
					Generate descriptions for all videos missing at least one description field.
					The system will use caption files to generate missing descriptions via AI.
				</Text>

				<Space direction="vertical" size="large" style={{ width: "100%", marginTop: 24 }}>
					<Form form={form} layout="vertical">
						<Form.Item 
							name="limit"
							label="Number of Videos to Process"
							rules={[
								{required: true, message: "Please enter a number of videos to process"}
							]}
						>
							<InputNumber
								style={{ width: "100%", maxWidth: 300 }}
								placeholder="Enter number of videos to process"
								min={1}
							/>
						</Form.Item>

						<Form.Item 
							name="createdAfterDate"
							label="Videos Created After (Optional)"
						>
							<DatePicker
								style={{ width: "100%", maxWidth: 300 }}
								format="YYYY-MM-DD"
								placeholder="Select date (leave blank for all dates)"
								allowClear
							/>
						</Form.Item>
						<LanguageSelectItem label="Audience Language" style={{ width: "100%", maxWidth: 300 }} name="languages" languages={languagesList} mode="multiple" />
						<SeriesSelectItem style={{ width: "100%", maxWidth: 300 }}  name="series" seriesData={seriesList} mode="multiple" />
					</Form>

					<Space>
						<Button
							type="primary"
							size="large"
							icon={<PlayCircleOutlined />}
							onClick={handleStartGeneration}
							loading={loading}
						>
							{operationId ? "Restart Generation" : "Start Bulk Description Generation"}
						</Button>
						
						{(() => {
							const statusStr = typeof jobStatus === 'string' 
								? jobStatus 
								: (jobStatus?.status || jobStatus);
							return (statusStr === "PROCESSING" || statusStr === "QUEUED" || statusStr === "PENDING") && (
								<Popconfirm
									title="Cancel this job?"
									description="Are you sure you want to cancel the bulk description generation? The current progress will be lost."
									onConfirm={handleCancelJob}
									okText="Yes, Cancel"
									cancelText="No"
								>
									<Button
										danger
										size="large"
										icon={<StopOutlined />}
									>
										Cancel Job
									</Button>
								</Popconfirm>
							);
						})()}
					</Space>

					{jobStatus && (
						<StatusCard>
							{getStatusMessage()}
							{(() => {
								const statusStr = typeof jobStatus === 'string' 
									? jobStatus 
									: (jobStatus?.status || jobStatus);
								const csvUrl = jobStatus?.csvUrl || jobStatus?.metadata?.csvUrl;
								
								return (statusStr === "COMPLETED" || statusStr === "COMPLETED_WITH_ERRORS") && csvUrl && (
									<Button
										type="primary"
										icon={<DownloadOutlined />}
										onClick={handleDownloadCSV}
										style={{ marginTop: 16 }}
									>
										Download CSV
									</Button>
								);
							})()}
							{polling && (
								<div style={{ marginTop: 16 }}>
									<Spin size="small" /> <Text>Checking status...</Text>
								</div>
							)}
						</StatusCard>
					)}
				</Space>
			</Card>
		</Container>
	);
};

export default BulkDescriptionsComponent;

